import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Icon from '../Icon/Icon'

const Control = styled.input`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0 1rem;
  padding-right: 4rem;
  font-weight: var(--font-weight);

  outline: 0;
  background-color: transparent;

  border-radius: var(--border-radius-form);
  border: var(--border-form);
  transition-property: border-color, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  ${props =>
    props.hasError &&
    css`
      border-color: var(--color-error);
    `}

  &:disabled {
    opacity: 0.5;
  }

  &:focus {
    border-color: var(--color-primary);
  }

  &::placeholder {
    opacity: 0;
  }

  &:required {
    box-shadow: none !important;
  }
`

const Placeholder = styled.span`
  display: flex;
  align-items: center;
  user-select: none;
  border-radius: var(--border-radius-form);

  color: var(--color-fg-light);
  position: absolute;

  transition-property: font-size, height, background-color, padding, top, color;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  background-color: var(--color-bg-form);
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.75em;
  top: 0;
  left: 0.75rem;
  pointer-events: none;
  overflow-x: visible;
  white-space: nowrap;

  &:empty {
    display: none;
  }

  ${Control}:placeholder-shown ~ & {
    background-color: transparent;
    top: 0.5rem;
    height: 3rem;
    padding: 0 0.25rem;
    font-size: 1em;
  }

  ${props =>
    props.hasError &&
    css`
      color: var(--color-error);
      ${Control}:placeholder-shown ~ & {
        color: var(--color-fg-light);
      }
    `}

  ${Control}:focus ~ & {
    background-color: var(--color-bg-form);
    color: var(--color-primary);
    height: 1rem;
    padding: 0 0.25rem;
    font-size: 0.75em;
    top: 0;
    left: 0.75rem;
  }
`

const ControlBase = styled.label`
  position: relative;
  display: block;
  width: 100%;
  height: 3.5rem;
  padding-top: 0.5rem;
  font-size: 1rem;
`

const ComponentBase = styled.div`
  margin-bottom: 0.625rem;
`

const ErrorMessage = styled.output`
  display: block;
  color: var(--color-error);
  padding: 0 1rem;
  font-size: 0.75em;
  margin-top: 0.25rem;
  overflow: hidden;
  height: 1.125rem;
  transition-property: height, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  :empty {
    height: 0;
    opacity: 0;
  }

  ${ControlBase}:focus-within ~ & {
    height: 0;
    opacity: 0;
  }
`

const ShowToggle = styled.span`
  cursor: pointer;
  color: var(--color-primary);
  position: absolute;
  right: 0;
  top: 0.5rem;
  height: 3rem;
  width: 4rem;
  text-align: center;
  font-size: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
`

class PasswordInput extends React.Component {
  input = React.createRef()

  state = {
    isPasswordShown: false,
  }

  componentDidMount() {
    const { initialValue } = this.props

    if (this.input.current) {
      this.input.current.value = initialValue
    }
  }

  componentDidUpdate(prevProps) {
    const { initialValue } = this.props

    if (prevProps.initialValue !== initialValue) {
      this.input.current.value = initialValue
    }
  }

  toggleShowPassword = e => {
    e.preventDefault()

    const { isPasswordShown } = this.state

    this.setState({
      isPasswordShown: !isPasswordShown,
    })
  }

  render() {
    const { placeholder, error, touched, type, ...props } = this.props
    const { isPasswordShown } = this.state

    return (
      <ComponentBase>
        <ControlBase>
          <Control
            type={isPasswordShown ? 'text' : 'password'}
            {...props}
            placeholder={placeholder}
            ref={this.input}
            hasError={touched && error}
            autoComplete="off"
          />
          <Placeholder hasError={touched && error}>{placeholder}</Placeholder>
          <ShowToggle onClick={this.toggleShowPassword}>
            <Icon
              name={!isPasswordShown ? 'password-unmask' : 'password-mask'}
            />
            {/* {isPasswordShown ? 'Hide' : 'Show'} */}
          </ShowToggle>
        </ControlBase>
        <ErrorMessage>{touched && error}</ErrorMessage>
      </ComponentBase>
    )
  }
}

PasswordInput.defaultProps = {
  placeholder: '',
  initialValue: '',
  error: null,
  touched: false,
}

PasswordInput.propTypes = {
  placeholder: PropTypes.string,
  initialValue: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
}

export default PasswordInput
